import { FC, Fragment, useCallback, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
// Types
import Features, { DatasetHPLFeatures } from 'app/types/Features';
// Models
import IManagementAccount from 'app/models/ManagementAccount';
// Store
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { getSubscriptions } from 'app/store/Subscriptions/Subscriptions.async';
import { updateAccount } from 'app/store/AccountsManagement/AccountsManagement.async';
// Actions
import { SubscriptionsActions } from 'app/store/Subscriptions/Subscriptions.slice';
// Selectors
import { selectHasAnyActiveSubscripionPackageCPTCodesAccess } from 'app/store/Subscriptions/Subscriptions.selectors';
import { selectLoading } from 'app/store/AccountsManagement/AccountsManagement.selectors';
// Mui
import { FormGroup } from '@mui/material';
// Components
import Dialog from 'app/components/Dialog';
import { Input, Select, Switch, Button } from 'app/components/Mui';
import { OutlineBlock } from 'app/components/Utilities';
// Utility
import { isRequired } from 'app/utilities/Validations';
// i18next
import { useTranslation } from 'react-i18next';

interface IFormData {
  quickBooksId: string;
  // Features
  datasetHPL: DatasetHPLFeatures | undefined;
  features: (Features)[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  account: IManagementAccount;
};

const AccountEditFormDialog:FC<Props> = ({
  // Props
  open, onClose, account
}) => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const hasAnyActiveSubscripionPackageCPTCodesAccess = useAppSelector(selectHasAnyActiveSubscripionPackageCPTCodesAccess);
  const loading:boolean = useAppSelector(selectLoading);

  const getDatasetHPLValue = useCallback(() => {
    const datasetHPLFeatures =  [Features.DatasetHPL_FL, Features.DatasetHPL_CA, Features.DatasetHPL_WA];

    if ( !account.features || !account.features.length ) return undefined;

    for ( let feature of datasetHPLFeatures ){
      if ( account.features.includes(feature) ) return feature as DatasetHPLFeatures;
    }

    return undefined;
    // eslint-disable-next-line
  }, [account.features]);

  const { control, handleSubmit, formState: { errors } } = useForm<IFormData>({
    defaultValues: {
      quickBooksId: account.quickBooksId,
      datasetHPL: getDatasetHPLValue(),
      features: account.features.filter((feature) => 
        feature !== Features.DatasetHPL_FL &&
        feature !== Features.DatasetHPL_WA &&
        feature !== Features.DatasetHPL_CA
      )
    }
  });

  useEffect(() => {
    dispatch(getSubscriptions({ accountId: account.id }));
    return () => {
      dispatch(SubscriptionsActions.setInitialField('subscriptions'));
    }
    // eslint-disable-next-line
  }, []);

  const onSubmit = handleSubmit(async (data:IFormData) => {
    try {
      const { features, datasetHPL, ...otherData } = data;
      const nextData:any = {
        ...otherData,
        features: features.length ? features : []
      };
      if ( datasetHPL ) nextData['features'] = [...nextData['features'], datasetHPL];
      await dispatch(updateAccount({ accountId: account.id, data: nextData })).unwrap();
      onClose();
    } catch(error){}
  });

  const actions = (
    <Fragment>
      <Button
        name="Cancel update company account dialog"
        onClick={onClose}
      >{t('buttons.close')}</Button>
      <Button
        name="Save company account dialog"
        loading={loading}
        onClick={onSubmit}
        variant="contained"
        color="primary"
      >{t('buttons.save')}</Button>
    </Fragment>
  );

  const datasetHPLOptions = [
    { id: '', name: t('pages.companyAccounts.chooseauthorMatchingDataset') },
    { id: Features.DatasetHPL_FL, name: 'FL' },
    { id: Features.DatasetHPL_WA, name: 'WA' },
    { id: Features.DatasetHPL_CA, name: 'CA' },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={t('pages.companyAccounts.updateFormTitle')}
      actions={actions}
    >
      <form noValidate>
        {/* Quickbooks ID */}
        <Controller
          control={control} name="quickBooksId"
          rules={{ required: isRequired }}
          render={({ field }) => (
            <Input
              {...field}
              label={t('labels.quickbooksId')}
              error={Boolean(errors.quickBooksId)}
              helperText={errors.quickBooksId ? errors.quickBooksId.message : ''}
              required
            />
          )}
        />
        <OutlineBlock sx={{ mt: 6 }} label={t('labels.features')}>
          <Fragment>
            <Controller
              control={control} name="features"
              render={({ field:{ onChange, value } }) => {
                const handleChange = (feature:Features) => () => {
                  const hasFeature = (value as Features[]).includes(feature);
                  let nextFeatures: Features[];

                  if (hasFeature) {
                    // removing feature
                    nextFeatures = (value as Features[]).filter((v) => v !== feature);
                    // if removing AIChat, also remove AIChatHidePlatformPrompts
                    if (feature === Features.AIChat) {
                      nextFeatures = nextFeatures.filter((v) => v !== Features.AIChatHidePlatformPrompts);
                    }
                  } else {
                    // adding feature
                    nextFeatures = [...(value as Features[]), feature];
                  }

                  onChange(nextFeatures);
                }
                return (
                  <FormGroup>
                    <Switch
                      name="Billing Hide Overdue Warnings"
                      label={t('labels.billingHideOverdueWarnings')}
                      value={value.includes(Features.BillingHideOverdueWarnings)}
                      onChange={handleChange(Features.BillingHideOverdueWarnings)}
                    />
                    <Switch
                      name="AI Chat"
                      label={t('labels.aiChat')}
                      value={value.includes(Features.AIChat)}
                      onChange={handleChange(Features.AIChat)}
                    />
                    {value.includes(Features.AIChat) ? (
                      <Switch
                        name="AI Chat Hide Platform Prompts"
                        label={t('labels.aiChatHidePlatformPrompts')}
                        value={value.includes(Features.AIChatHidePlatformPrompts)}
                        onChange={handleChange(Features.AIChatHidePlatformPrompts)}
                      />
                    ) : null}
                    {hasAnyActiveSubscripionPackageCPTCodesAccess ? (
                      <Switch
                        name="Cpt codes access"
                        label={t('pages.companyAccounts.cptCodesAccess')}
                        value={value.includes(Features.HcpcsCodes)}
                        onChange={handleChange(Features.HcpcsCodes)}
                        helperText={t('pages.companyAccounts.cptCodesAccessHelperText')}
                      />
                    ) : null}
                    <Switch
                      name="Automatic page classification"
                      label={t('pages.companyAccounts.automaticPageClassification')}
                      value={value.includes(Features.PageClassification)}
                      onChange={handleChange(Features.PageClassification)}
                      helperText={t('pages.companyAccounts.automaticPageClassificationHelperText')}
                    />
                    <Switch
                      name="Patient portal"
                      label={t('labels.patientPortal')}
                      value={value.includes(Features.PatientPortal)}
                      onChange={handleChange(Features.PatientPortal)}
                    />
                  </FormGroup>
                )
              }}
            />
            <Controller
              control={control} name="datasetHPL"
              render={({ field }) => (
                <Select
                  {...field}
                  label={t('pages.companyAccounts.authorMatchingDataset')}
                  options={datasetHPLOptions}
                />
              )}
            />
          </Fragment>
        </OutlineBlock>
      </form>
    </Dialog>
  );
}

export default AccountEditFormDialog;
